import { BASE_URL } from '../../../configs'
import { createJsonHeaders, logoutOnRequestOrResponseJson } from '../../../api'

const packagesUrl = (suffix = '') => `${BASE_URL}/packages${suffix}`

const parseEnvelope = json => ({
  message: json?.message || '',
  data: json?.data ?? null,
  meta: json?.meta ?? null,
})

const request = (path, { method = 'GET', body, signal } = {}) =>
  fetch(packagesUrl(path), {
    credentials: 'include',
    headers: createJsonHeaders(),
    method,
    signal,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  }).then(response => logoutOnRequestOrResponseJson(response).then(parseEnvelope))

/**
 * List packages (one page). Prefer {@link listAllPackages} for admin UI lists.
 */
export function listPackages({ status, page = 0, size = 20, signal } = {}) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('size', String(size))
  if (status) params.set('status', status)

  return request(`?${params.toString()}`, { signal })
}

/** Walk every page so pickers / the builder see the full catalog. */
export async function listAllPackages({ status, signal } = {}) {
  const size = 100
  let page = 0
  let totalPages = 1
  const all = []

  while (page < totalPages) {
    const { data, meta } = await listPackages({ status, page, size, signal })
    if (Array.isArray(data)) all.push(...data)
    totalPages = typeof meta?.totalPages === 'number' ? meta.totalPages : 1
    page += 1
    if (!meta) break
  }

  return all
}

export function getPackage(packageId, signal) {
  return request(`/${packageId}`, { signal })
}

export function createPackage(body) {
  return request('', { method: 'POST', body })
}

/**
 * Builder Save is a draft. Create is always DRAFT on the server — do not
 * follow POST with a status PATCH (that used to force INACTIVE under the
 * older ACTIVE-on-create contract).
 */
export function createPackageAsDraft(body) {
  return createPackage(body).then(({ data, message }) => {
    if (!data) throw new Error(message || 'Package save returned no data.')
    return { data, message: message || 'Package saved as draft.' }
  })
}

export function updatePackage(packageId, body) {
  return request(`/${packageId}`, { method: 'PUT', body })
}

export function patchSupervision(packageId, supervision) {
  return request(`/${packageId}/supervision`, { method: 'PATCH', body: supervision })
}

export function patchUsageLimit(packageId, usageLimit) {
  return request(`/${packageId}/usage-limit`, { method: 'PATCH', body: usageLimit })
}

export function patchStatus(packageId, status) {
  return request(`/${packageId}/status`, { method: 'PATCH', body: { status } })
}

export function deletePackage(packageId) {
  return request(`/${packageId}`, { method: 'DELETE' })
}

/**
 * Bots affected by editing this package (call before PUT update).
 * Envelope: { message, data: { packageId, packageName, assignedBotCount, botsToUpdate, botsToSkip } }
 */
export function getPackageEditImpact(packageId, signal) {
  return request(`/${packageId}/edit-impact`, { signal }).then(({ data, message }) => {
    if (!data) throw new Error(message || 'Could not load package edit impact.')
    return data
  })
}
